import {
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { FaQuestionCircle, FaUpload } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import { formatCyDate, notif } from '../../utils/util';
import { getEuStats, getEuImportStatus, startEuImport } from '../../../server/server';
import HelpPageModal from '../../modals/HelpPageModal';
import ImportHistoryList from './ImportHistoryList';
import ImportBatchMeasures from './ImportBatchMeasures';

const POLL_INTERVAL = 2000;

function EuInsert() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selSet, setSelSet] = useState<any>(null);
  const [importStarted, setImportStarted] = useState(false);
  const [helpModal, setHelpModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<any>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  // Import progress state
  const [importStatus, setImportStatus] = useState<any>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [stats, setStats] = useState<any>({
    date: '2023-04-16T21:09:55.731Z',
    groupNo: 0,
    groupSetName: '',
    name: '',
    totalRows: 0,
    updatedRows: 0,
    addedRows: 0,
  });

  // Sync selectedBatch from URL query param
  useEffect(() => {
    const batchId = searchParams.get('batchId');
    if (batchId) {
      setSelectedBatch({ id: parseInt(batchId, 10) });
    } else {
      setSelectedBatch(null);
    }
  }, [searchParams]);

  const handleBatchClick = (row: any) => {
    setSelectedBatch(row);
    setSearchParams({ batchId: String(row.id) });
  };

  const handleBackToHistory = () => {
    setSelectedBatch(null);
    setSearchParams({});
  };

  const closeHelpModal = () => {
    setHelpModal(false);
  };

  const updateProgress = (data: any) => {
    setImportStatus(data);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const refreshStats = async () => {
    await loadEuStats();
  };

  const startPolling = () => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const resp = await getEuImportStatus();
        if (resp?.status === 200 && resp.data) {
          updateProgress(resp.data);

          if (resp.data.status === 'finished') {
            stopPolling();
            setImportStarted(false);
            notif(
              'Увоз завршен: додато ' + (resp.data.addedRows ?? 0) +
              ', измењено ' + (resp.data.updatedRows ?? 0) + ' редова',
              'success'
            );
            refreshStats();
            setHistoryRefreshKey((k) => k + 1);
          } else if (resp.data.status === 'failed') {
            stopPolling();
            setImportStarted(false);
            notif('Увоз није успео', 'error');
            refreshStats();
          }
        } else {
          stopPolling();
          setImportStarted(false);
        }
      } catch {
        stopPolling();
        setImportStarted(false);
      }
    }, POLL_INTERVAL);
  };

  const handleSubmit = async () => {
    if (selSet) {
      setImportStarted(true);
      setImportStatus(null);
      try {
        await startEuImport(parseInt(selSet));
        startPolling();
      } catch {
        setImportStarted(false);
        notif('Грешка при покретању увоза', 'error');
      }
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, []);

  const loadEuStats = async () => {
    const resp = await getEuStats();
    if (resp.status === 200) {
      setStats(resp?.data);
    }
  };

  useEffect(() => {
    loadEuStats();
  }, []);

  const progressPercent =
    importStatus?.totalRows > 0
      ? Math.round((importStatus.processedRows / importStatus.totalRows) * 100)
      : 0;

  return (
    <div className="py-10 px-2 overflow-auto gray-small-scroll h-full">
      <div className="flex gap-6 justify-between pr-10">
        <h1 className="text-[#04438b] text-4xl font-bold">Увоз ЕУ прописа</h1>
        <div
          className="flex items-center justify-between gap-3 rounded-full border border-[#81848f] px-2 text-[#60626a] h-[25px] w-fit hover:bg-[#303135] hover:text-white cursor-pointer"
          onClick={() => setHelpModal(true)}
        >
          <p className="rounded-full flex items-center justify-center">
            <FaQuestionCircle />
          </p>
          <p className="">Упутство</p>
        </div>
      </div>
      <div className="flex w-full gap-6">
        <div className="rounded-lg border border-[#81848f3d] w-[500px] p-6 pb-4 mt-8 flex flex-col justify-between">
          <h1 className="text-[#04438bae] text-3xl font-bold mb-6">
            Увоз европских прописа
          </h1>
          <FormControl size="small" sx={{ minWidth: 310 }}>
            <InputLabel id="demo-select-small">Груписано по</InputLabel>
            <Select
              labelId="demo-select-small"
              id="demo-select-small"
              value={selSet ?? ''}
              size="small"
              label="Груписано по"
              onChange={(e) => setSelSet(e.target.value)}
            >
              <MenuItem value="">
                <em>Одабир</em>
              </MenuItem>
              <MenuItem key="2" value="2">
                <strong>2.</strong> Directory
              </MenuItem>
              <MenuItem key="84" value="84">
                <strong>84.</strong> Screening new
              </MenuItem>
            </Select>
          </FormControl>
          <div className="w-full mt-12">
            {importStarted ? (
              <div className="space-y-3">
                {!importStatus?.totalRows ? (
                  <>
                    <LinearProgress />
                    <p className="text-[#04438b] text-sm text-center">
                      Увоз у току... (Овај процес може трајати неколико минута.)
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm text-[#04438b]">
                      <span>
                        Увезено {importStatus.processedRows?.toLocaleString()} / {importStatus.totalRows?.toLocaleString()}
                      </span>
                      <span className="font-bold">{progressPercent}%</span>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={progressPercent}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#e8edf5',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          backgroundColor: '#1D4B8E',
                        },
                      }}
                    />
                    <div className="flex justify-between text-xs text-[#60626a]">
                      <span>Додато: {importStatus.addedRows ?? 0}</span>
                      <span>Измењено: {importStatus.updatedRows ?? 0}</span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex justify-end">
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#1D4B8E' }}
                  endIcon={<FaUpload />}
                  onClick={handleSubmit}
                  disabled={!selSet}
                >
                  Потврди
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="rounded-lg border border-[#81848f3d] w-[500px] p-6 pb-4 mt-8">
          <h1 className="text-[#04438bae] text-3xl font-bold mb-6">
            Статистика и хронологија увоза
          </h1>

          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Последњи пут увезено:{' '}
            <span className="font-bold">{formatCyDate(stats?.date)}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Груписано по:{' '}
            <span className="font-bold">{stats?.groupSetName}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Увоз извршио корисник:{' '}
            <span className="font-bold">{stats?.name}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Укупан број додатих редова:{' '}
            <span className="font-bold">{stats?.addedRows}</span>
          </p>
          <p className="text-[#0a395bbd] text-lg font-medium mb-2">
            Укупан број измењених редова:{' '}
            <span className="font-bold">{stats?.updatedRows}</span>
          </p>
        </div>
      </div>
      <div className="mt-5 w-full">
        {selectedBatch ? (
          <ImportBatchMeasures batch={selectedBatch} onBack={handleBackToHistory} />
        ) : (
          <ImportHistoryList onRowClick={handleBatchClick} refreshKey={historyRefreshKey} />
        )}
      </div>
      <HelpPageModal
        modalIsOpen={helpModal}
        closeModal={closeHelpModal}
        name={'insertEU'}
      />
    </div>
  );
}

export default EuInsert;
